import Debug from 'debug';
import { ExamResultService } from '@/app/exam/service';
import { ExamSessionService } from '@/app/exam-session/service';
import { isExamPassed, calculateExamPercent, calculateExamResultWithoutNegative } from '@/app/exam/domain';
import { ExamStateMachineAbstractState, ExamStateMachineCommonEvents } from './state'
import { ExamStateMachineData, ExamQuestionAnswer, ExamQuestionEvaluationResult } from '../data'
import { ExamQuestionState } from '../data/exam-question';
import { QuestionStateMachine } from '@/app/question/domain/state-machine';
import { createQuestionStateMachine } from '@/app/quiz-player/api/state-machine';

const debug = Debug('exam-in-progress-state')

interface Events extends ExamStateMachineCommonEvents {
  onFinished: (details: InProgressFinishedDetails) => void
}

export interface InProgressFinishedDetails {
  isTimeExpired: boolean
}

export class ExamInProgressState extends ExamStateMachineAbstractState<Events> {

  public readonly name = 'in-progress'

  private timerInterval: number
  private isInState = false
  private currentQuestionStateMachine?: QuestionStateMachine

  constructor(private state: ExamStateMachineData,
              private examResultService: ExamResultService,
              private examSessionService: ExamSessionService) {
    super()
  }

  enter() {
    debug('enter')
    this.registerQuestionShownTime()

    this.startTimer()
    this.isInState = true

    this.createCurrentQuestionStateMachine()
  }

  exit() {
    debug('exit')
    this.stopTimer()
    this.isInState = false
  }

  cancel() {
    debug('cancel')
    this.beginCancel()
    this.endCancel()
  }

  private startTimer() {
    if (!this.state.hasTimeLimit) { return }

    this.timerInterval = setInterval(() => {
      this.refreshRemainingTime()
    }, 300)
  }

  private stopTimer() {
    if (!this.state.hasTimeLimit) { return }

    clearInterval(this.timerInterval)
  }

  navigate(questionIndex: number) {
    const success = this.state.updateCurrentQuestion(questionIndex, this.navigationOptions)
    if (success) {
      this.afterNavigationHook()
    }
  }

  navigateNext() {
    const success = this.state.goToNextQuestion(this.navigationOptions)
    if (success) {
      this.afterNavigationHook()
    }
  }

  navigatePrevious() {
    const success = this.state.goToPreviousQuestion(this.navigationOptions)
    if (success) {
      this.afterNavigationHook()
    }
  }

  private afterNavigationHook() {
    this.registerQuestionShownTime()
    this.updateSessionNavigation()
    this.updateSessionQuestionTimes()

    this.createCurrentQuestionStateMachine()
  }

  private registerQuestionShownTime() {
    this.state.registerQuestionShownTime(new Date())
  }

  private get navigationOptions() {
    return { updateLastIndex: true }
  }

  changeState(state: ExamQuestionState) {
    const { stateMachine, questionIndex } = this.resolveCurrentStateMachine()
    stateMachine.updateState(state)

    this.updateSessionState(questionIndex)
  }

  changeAnswer(answer: ExamQuestionAnswer, evaluation: ExamQuestionEvaluationResult) {

    const { stateMachine, questionIndex } = this.resolveCurrentStateMachine()

    stateMachine.updateAnswer(answer)
    stateMachine.updateEvaluation(evaluation)

    this.updateSessionState(questionIndex)
  }

  resetQuestion() {
    const { stateMachine } = this.resolveCurrentStateMachine()

    stateMachine.reset()
  }

  private refreshRemainingTime() {
    if (!this.state.hasTimeLimit || !this.isInState) { return }
    
    const remaining = this.state.refreshRemainingTimeSeconds()

    if (remaining <= 0) {
      this.finalize(true)
    }
  }

  finish() {
    this.finalize(false)
  }

  private finalize(timeExpired: boolean) {
    debug('store', timeExpired)

    this.state.finalizeQuestionTimes()
    this.setAllQuestionsAsEvaluated()
    const examDetails = this.state.examDetails

    debug('question scores', this.state.questionScores)
    
    const score = calculateExamResultWithoutNegative(this.state.questionScores)
    const { isPassed } = isExamPassed(score, examDetails.percentToPass)
    const time = this.calculateTime(examDetails.timeLimitSeconds)

    debug('finish', score, time)

    const state = {
      score: {
        min: score.min,
        max: score.max,
        raw: score.raw,
        percent: calculateExamPercent(score)
      },
      passed: isPassed,
    }
    this.state.finished(state)

    this.examResultService.finishExam({
      id: examDetails.id,
      score,
      durationSeconds: time.durationSeconds
    })

    this.finalizeSession()

    this.triggerEvent(events => events.onFinished({ isTimeExpired: timeExpired }))
  }

  private setAllQuestionsAsEvaluated() {
    for (let index = 0; index < this.state.questionCount; index++) {
      const stateMachine = this.createQuestionStateMachine(index)
      stateMachine.evaluate()
      stateMachine.showSolution()
    }
  }

  private createCurrentQuestionStateMachine() {
    const stateMachine = this.createQuestionStateMachine(this.state.questionIndex)
    this.currentQuestionStateMachine = stateMachine
  }

  private createQuestionStateMachine(questionIndex: number) {
    const { question, state } = this.state.questionForStateMachine(questionIndex)

    return createQuestionStateMachine(question, state)
  }

  private resolveCurrentStateMachine() {
    if (!this.currentQuestionStateMachine) {
      throw new Error(`Coding error: current question state machine has not been set.`)
    }

    const questionIndex = this.state.questionIndex

    return {
      stateMachine: this.currentQuestionStateMachine,
      questionIndex: questionIndex
    }
  }

  private finalizeSession() {
    this.updateSessionQuestionTimes()
    this.examSessionService.finishSession(this.state.currentSessionId)
  }

  private calculateTime(timeLimitSeconds: number) {
    const now = new Date()
    let durationSeconds = this.state.durationSeconds(now)
    if (this.state.hasTimeLimit && durationSeconds > timeLimitSeconds) {
      durationSeconds = timeLimitSeconds
    }

    return {
      finishTime: now,
      durationSeconds: durationSeconds
    }
  }

  private updateSessionNavigation() {
    this.examSessionService.updateNavigation(this.state.currentSessionId, this.state.exportNavigationState())
  }

  private updateSessionQuestionTimes() {
    this.examSessionService.updateQuestionTimes(this.state.currentSessionId, {
      questionTimes: this.state.exportQuestionTimesState()
    })
  }

  private updateSessionState(questionIndex: number) {
    const state = this.state.exportQuestionStateAt(questionIndex)
    this.examSessionService.updateQuestionState(this.state.currentSessionId, state)
  }
}

